import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * The UI reads only from here (ADR-0003) — the network layer (ws-client)
 * writes into it. Makes the app render instantly on open and work offline
 * for free, without a separate in-memory cache to keep in sync.
 */

export type StoredMedia = {
  id: string;
  mime: string;
  bytes?: number;
  width?: number;
  height?: number;
  /** data: URL, inline in the payload (ADR-0005/0006) — the grid renders straight from this, no fetch. */
  thumb?: string;
};

export type StoredMessage = {
  id: string;
  convId: string;
  senderId: string;
  ts: number;
  kind: 'text' | 'image';
  text: string;
  media?: StoredMedia[];
  replyTo?: string;
  deliveredAt?: number;
  readAt?: number;
  /** True for a message this device sent but hasn't gotten an ack for yet. */
  pending?: boolean;
};

interface FoyerDB extends DBSchema {
  messages: {
    key: string;
    value: StoredMessage;
    indexes: { byConv: [string, number] };
  };
}

let dbPromise: Promise<IDBPDatabase<FoyerDB>> | null = null;

function getDb(): Promise<IDBPDatabase<FoyerDB>> {
  dbPromise ??= openDB<FoyerDB>('foyer', 1, {
    upgrade(db) {
      const store = db.createObjectStore('messages', { keyPath: 'id' });
      store.createIndex('byConv', ['convId', 'ts']);
    },
  });
  return dbPromise;
}

export async function putMessage(msg: StoredMessage): Promise<void> {
  const db = await getDb();
  await db.put('messages', msg);
}

export async function getMessages(convId: string): Promise<StoredMessage[]> {
  const db = await getDb();
  const range = IDBKeyRange.bound([convId, -Infinity], [convId, Infinity]);
  return db.getAllFromIndex('messages', 'byConv', range);
}

export async function markReceipt(
  id: string,
  kind: 'delivered' | 'read',
  at: number,
): Promise<void> {
  const db = await getDb();
  const existing = await db.get('messages', id);
  if (!existing) return; // receipt for a message not yet persisted locally; safe to drop
  existing[kind === 'delivered' ? 'deliveredAt' : 'readAt'] = at;
  await db.put('messages', existing);
}

/** Clears `pending` once the server acks a send — without this, a message
 * stays "pending" forever and gets resent on every reconnect (found live). */
export async function clearPending(id: string): Promise<void> {
  const db = await getDb();
  const existing = await db.get('messages', id);
  if (!existing || !existing.pending) return;
  existing.pending = false;
  await db.put('messages', existing);
}
